import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Badge, Row, Col, Alert } from 'react-bootstrap';

const CopyItemsSelector = ({ 
  sourceMenuId, 
  selectedItems, 
  onSelectionChange, 
  copyAllItems, 
  onCopyAllChange, 
  canEdit, 
  getMenuItems 
}) => {
  const [sourceItems, setSourceItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sourceMenuId) {
      loadSourceItems();
    }
  }, [sourceMenuId]);

  const loadSourceItems = async () => {
    setLoading(true);
    setError('');
    try {
      const items = await getMenuItems(sourceMenuId);
      setSourceItems(items);
    } catch (err) {
      setError('Не удалось загрузить блюда из выбранного меню');
    } finally {
      setLoading(false);
    }
  };

  const handleItemToggle = (itemId) => {
    if (copyAllItems) return; // Don't allow individual selection when copying all
    
    const newSelection = selectedItems.includes(itemId)
      ? selectedItems.filter(id => id !== itemId)
      : [...selectedItems, itemId];
    
    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    if (copyAllItems) {
      onCopyAllChange(false);
      onSelectionChange([]);
    } else {
      onCopyAllChange(true);
      onSelectionChange([]);
    }
  };

  const handleSelectNone = () => {
    onCopyAllChange(false);
    onSelectionChange([]);
  };

  const handleSelectVisible = () => {
    onCopyAllChange(false);
    onSelectionChange(sourceItems.map(item => item._id));
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm me-2" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
        Загрузка блюд...
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (sourceItems.length === 0) {
    return (
      <Alert variant="info">
        В выбранном меню нет блюд для копирования
      </Alert>
    );
  }

  // Group items by category
  const itemsByCategory = {};
  sourceItems.forEach(item => {
    const category = item.category || 'Без категории';
    if (!itemsByCategory[category]) {
      itemsByCategory[category] = [];
    }
    itemsByCategory[category].push(item);
  });

  return (
    <div className="border rounded p-3 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          Выберите блюда для копирования ({sourceItems.length} всего)
        </h6>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant={copyAllItems ? "success" : "outline-primary"}
            onClick={handleSelectAll}
            disabled={!canEdit}
          >
            {copyAllItems ? '✓ Все выбраны' : 'Выбрать все'}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleSelectVisible}
            disabled={!canEdit || copyAllItems}
          >
            Выбрать видимые
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={handleSelectNone}
            disabled={!canEdit}
          >
            Снять выбор
          </Button>
        </div>
      </div>

      <div className="mb-3">
        <Form.Check
          type="checkbox"
          id="copy-all-items"
          label="Копировать все блюда из меню"
          checked={copyAllItems}
          onChange={(e) => onCopyAllChange(e.target.checked)}
          disabled={!canEdit}
        />
      </div>

      {!copyAllItems && (
        <div className="max-height-400 overflow-auto">
          {Object.entries(itemsByCategory || {}).map(([categoryName, items]) => (
            <div key={categoryName} className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <span className="me-2">📂</span>
                <h6 className="mb-0">{categoryName}</h6>
                <Badge bg="secondary" className="ms-2">{items.length}</Badge>
              </div>
              
              <Row className="g-2">
                {items.map((item) => (
                  <Col md={6} key={item._id}>
                    <Card 
                      className={`border ${selectedItems.includes(item._id) ? 'border-success bg-light' : ''}`}
                      style={{ cursor: canEdit ? 'pointer' : 'not-allowed' }}
                      onClick={() => canEdit && handleItemToggle(item._id)}
                    >
                      <Card.Body className="p-2">
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={selectedItems.includes(item._id)}
                            onChange={() => handleItemToggle(item._id)}
                            disabled={!canEdit}
                            className="me-2"
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold small">{item.name}</div>
                            <div className="text-muted small">
                              {item.price.toFixed(2)} BYN
                              {item.weight && ` • ${item.weight} ${item.weightUnit || 'г'}`}
                            </div>
                          </div>
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{ 
                                width: '40px', 
                                height: '40px', 
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </div>
      )}

      {copyAllItems && (
        <Alert variant="success">
          <strong>Будут скопированы все блюда:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(itemsByCategory || {}).map(([categoryName, items]) => (
              <li key={categoryName}>
                {categoryName}: {items.length} блюд
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {!copyAllItems && selectedItems.length > 0 && (
        <Alert variant="info">
          <strong>Выбрано для копирования:</strong> {selectedItems.length} блюд
        </Alert>
      )}
    </div>
  );
};

export default CopyItemsSelector;
